import { Box, Stack, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius, motion } from '@liminal/brand/scale';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { WarningCard } from '@liminal/web-ui/feedback/WarningCard';
import type { MerchantImageUploadItem, UploadState } from '@/hooks/useMerchantImages';

/** What an upload on its way is doing, in the operator's words. */
const IN_FLIGHT_COPY: Record<Exclude<UploadState['phase'], 'attached' | 'failed'>, string> = {
  idle: 'Waiting…',
  resizing: 'Scaling it down…',
  requesting_url: 'Getting an upload link…',
  uploading: 'Uploading…',
  attaching: 'Adding it to the partner…',
};

interface ImageUploadRowProps {
  readonly upload: MerchantImageUploadItem;
  readonly onRetry: () => void;
  readonly onDismiss: () => void;
}

/** One picked file on its way to the partner: its progress, or why it failed and a way to try again. */
export function ImageUploadRow({ upload, onRetry, onDismiss }: ImageUploadRowProps) {
  const { state, fileName } = upload;
  if (state.phase === 'attached') return null;

  if (state.phase === 'failed') {
    return (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <WarningCard
            tone="error"
            compact
            action={state.resume ? { label: 'Try again', onClick: onRetry } : undefined}
            data-testid={`upload ${fileName} failed`}
          >
            {state.message}
          </WarningCard>
        </Box>
        <IconButton icon="close" label={`Dismiss ${fileName}`} size="sm" tone="muted" onClick={onDismiss} />
      </Stack>
    );
  }

  const percent = state.phase === 'uploading' ? Math.round(state.progress * 100) : null;
  return (
    <Stack spacing={0.5} data-testid={`upload ${fileName}`}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {percent === null ? IN_FLIGHT_COPY[state.phase] : `${percent}%`}
        </Typography>
      </Stack>
      <Box
        role="progressbar"
        aria-label={`Uploading ${fileName}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent ?? undefined}
        sx={{
          height: 4,
          borderRadius: `${borderRadius.full}px`,
          overflow: 'hidden',
          bgcolor: webColors.surface.secondary,
        }}
      >
        {/* Only the PUT reports how far it has got; the other steps show an empty track and their words above. */}
        <Box sx={{ width: `${percent ?? 0}%`, height: '100%', bgcolor: webColors.state.info, transition: `width ${motion.duration.instant}ms linear` }} />
      </Box>
    </Stack>
  );
}
